import { Button } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { type Accept, type FileWithPath, useDropzone } from "react-dropzone";

import useTranslation from "../../hooks/useTranslation";

import style from "./DropArea.module.css";

interface DropZoneProps {
  maxFilesReached?: boolean;
  disabled?: boolean;
  maxFiles: number;
  maxFileSizeInMB: number;
  acceptedFileExtensions: string[];
  onChange: (files: FileWithPath) => void;
}

const DropArea: React.FC<DropZoneProps> = ({
  maxFiles,
  maxFileSizeInMB,
  acceptedFileExtensions,
  disabled,
  maxFilesReached,
  onChange,
}) => {
  const { t, m } = useTranslation();
  const isDisabled = disabled || maxFilesReached;
  const accept: Accept = {
    "application/octet-stream": acceptedFileExtensions.map((ext) => `.${ext}`),
  };

  const { acceptedFiles, fileRejections, isDragActive, open, getRootProps, getInputProps } =
    useDropzone({
      accept,
      maxSize: maxFileSizeInMB * 1024 * 1024,
      multiple: false,
      noClick: true,
      noKeyboard: true,
      disabled: isDisabled,
      onDropAccepted: (files) => {
        onChange(files[0]);
      },
      onDragOver: () => void 0,
      onDragLeave: () => void 0,
      onDragEnter: () => void 0,
    });

  const getTopText = () => {
    if (maxFilesReached) return t(m.FileUpload.MaxFilesReached, { maxFiles });
    if (acceptedFiles.length) return t(m.FileUpload.UploadSuccessful);
    return t(m.FileUpload.DragHereToUpload);
  };

  const getErrorMessage = () => {
    const error = fileRejections?.[0]?.errors?.[0];
    if (isDisabled || !error) return undefined;

    switch (error.code) {
      case "file-too-large": {
        return t(m.FileUpload.ErrorFileTooLarge, { maxFileSizeInMB });
      }
      case "file-invalid-type": {
        return t(m.FileUpload.ErrorFileInvalidType, {
          acceptedTypes: acceptedFileExtensions.join(", "),
        });
      }
      case "too-many-files": {
        return t(m.FileUpload.ErrorTooManyFiles);
      }
      default: {
        return t(m.FileUpload.ErrorUnknown);
      }
    }
  };

  return (
    <div
      {...getRootProps({
        className: classNames(
          style.dropZone,
          { [style.dragActive]: isDragActive },
          { [style.disabled]: isDisabled }
        ),
      })}
    >
      <input data-testid={"drop-area-input"} {...getInputProps()} />
      {!isDisabled && isDragActive && <p>{t(m.FileUpload.ReleaseFileToUpload)}</p>}
      {!isDragActive && (
        <>
          <p className={style.dropZoneText}>{getTopText()}</p>
          <Button type="button" variant="grayscale" onClick={open} disabled={isDisabled}>
            {t(m.FileUpload.BrowseFiles)}
          </Button>
          <p className={style.errorText}>{getErrorMessage()}</p>
        </>
      )}
    </div>
  );
};

export default DropArea;
